import React, { useState, useEffect, useMemo, useRef } from 'react'
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { t } from '../../components/i18n/i18n'
import back from '../../assets/images/elements/archiveHeader.jpg'

// ── Category metadata ─────────────────────────────────────────────────────────

export const CATEGORY_META = {
  sermons:     { labelUk: 'Проповіді',       color: '#c0622a' },
  bible_study: { labelUk: 'Вивчення Біблії', color: '#b58a00' },
  events:      { labelUk: 'Події',           color: '#3a8fd9' },
  choir:       { labelUk: 'Хор',             color: '#7c5cbf' },
  streams:     { labelUk: 'Трансляції',      color: '#3aaa6e' },
}

const CATEGORIES = Object.entries(CATEGORY_META).map(([key, meta]) => ({ key, ...meta }))

const LEGACY_CATEGORY_MAP = {
  sermon: 'sermons',
  word:   'bible_study',
  online: 'streams',
  choir:  'choir',
  events: 'events',
}

const extractVideoId = (link = '') => {
  const m = link.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : ''
}

// ── Date formatter ────────────────────────────────────────────────────────────

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch {
    return ''
  }
}

// ── VideoCard ─────────────────────────────────────────────────────────────────

const VideoCard = ({ video, speakerNames, mobile }) => {
  const [thumbSrc, setThumbSrc] = useState(
    video.thumbnail ||
    (video.videoId
      ? `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`
      : null)
  )

  const catMeta = CATEGORY_META[video.category] || null
  const dateStr = formatDate(video.date)

  const handleClick = () => {
    if (video.link) window.open(video.link, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      onClick={handleClick}
      style={{
        background: '#fff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(92,61,46,0.12)',
        border: '1px solid #f0e4d4',
        cursor: video.link ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.18s, transform 0.18s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(92,61,46,0.22)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(92,61,46,0.12)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Thumbnail */}
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt={video.titleUk || ''}
          onError={() =>
            video.videoId
              ? setThumbSrc(`https://img.youtube.com/vi/${video.videoId}/default.jpg`)
              : setThumbSrc(null)
          }
          style={{
            width: '100%',
            aspectRatio: '16/9',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            aspectRatio: '16/9',
            background: '#e8d5c0',
            borderRadius: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: '32px', opacity: 0.5 }}>▶</span>
        </div>
      )}

      {/* Card body */}
      <div
        style={{
          padding: mobile ? '12px' : '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
        }}
      >
        {/* Category badge */}
        {catMeta && (
          <span
            style={{
              alignSelf: 'flex-start',
              background: catMeta.color,
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
              padding: '3px 10px',
              borderRadius: '20px',
              letterSpacing: '0.3px',
              fontFamily: '"Segoe UI", system-ui, sans-serif',
            }}
          >
            {catMeta.labelUk}
          </span>
        )}

        {/* Title */}
        <div
          style={{
            fontSize: mobile ? '14px' : '15px',
            fontWeight: '700',
            color: '#2d1f14',
            lineHeight: '1.4',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
          }}
        >
          {video.titleUk || video.titleEn || ''}
        </div>

        {/* Bible reference */}
        {video.bible ? (
          <div
            style={{
              fontSize: '12px',
              color: '#8b6a50',
              fontStyle: 'italic',
              fontFamily: '"Segoe UI", system-ui, sans-serif',
            }}
          >
            {video.bible}
          </div>
        ) : null}

        {/* Speaker names — new speakerIds or legacy author string */}
        {(speakerNames.length > 0 || video.speakerNote || video._authorLegacy) && (
          <div
            style={{
              fontSize: '13px',
              color: '#5c3d2e',
              fontFamily: '"Segoe UI", system-ui, sans-serif',
            }}
          >
            {speakerNames.length > 0
              ? speakerNames.join(', ')
              : (video.speakerNote || video._authorLegacy)}
          </div>
        )}

        {/* Date */}
        {dateStr ? (
          <div
            style={{
              fontSize: '12px',
              color: '#a07850',
              marginTop: 'auto',
              paddingTop: '4px',
              fontFamily: '"Segoe UI", system-ui, sans-serif',
            }}
          >
            {dateStr}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// ── ArchiveHero ───────────────────────────────────────────────────────────────

const ArchiveHero = ({ mobile }) => (
  <div style={{ position: 'relative', width: '100%', height: mobile ? '15vh' : '20vh', overflow: 'hidden' }}>
    <img src={back} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, rgba(18,8,3,0.45) 0%, rgba(45,31,20,0.7) 100%)',
      }}
    />
    <h1
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        fontSize: mobile ? '24px' : '36px',
        fontWeight: '800',
        color: '#fff',
        textShadow: '0 2px 16px rgba(0,0,0,0.6)',
        letterSpacing: '0.5px',
        fontFamily: '"Segoe UI", system-ui, sans-serif',
        textAlign: 'center',
        padding: '0 24px',
      }}
    >
      {(() => { const v = t('archiveHeader'); return v === 'archiveHeader' ? 'Відеоархів' : v })()}
    </h1>
  </div>
)

// ── CategoryPills ─────────────────────────────────────────────────────────────

const CategoryPills = ({ activeCategory, onToggle }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
    <button
      onClick={() => onToggle('')}
      style={{
        padding: '7px 16px',
        borderRadius: '20px',
        border: '1.5px solid #c0622a',
        background: activeCategory === '' ? '#c0622a' : '#f5ece0',
        color: activeCategory === '' ? '#fff' : '#5c3d2e',
        fontSize: '13px',
        fontWeight: '700',
        cursor: 'pointer',
        fontFamily: '"Segoe UI", system-ui, sans-serif',
        transition: 'background 0.15s, color 0.15s',
      }}
    >
      Всі
    </button>
    {CATEGORIES.map(cat => (
      <button
        key={cat.key}
        onClick={() => onToggle(cat.key)}
        style={{
          padding: '7px 16px',
          borderRadius: '20px',
          border: `1.5px solid ${cat.color}`,
          background: activeCategory === cat.key ? cat.color : '#f5ece0',
          color: activeCategory === cat.key ? '#fff' : '#5c3d2e',
          fontSize: '13px',
          fontWeight: '700',
          cursor: 'pointer',
          fontFamily: '"Segoe UI", system-ui, sans-serif',
          transition: 'background 0.15s, color 0.15s',
        }}
      >
        {cat.labelUk}
      </button>
    ))}
  </div>
)

// ── YearButtons ───────────────────────────────────────────────────────────────

const YEARS_VISIBLE = 4

const YearButtons = ({ years, activeYear, onSelect }) => {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? years : years.slice(0, YEARS_VISIBLE)
  const hidden  = years.length - YEARS_VISIBLE

  const btnStyle = (active) => ({
    flexShrink: 0,
    padding: '7px 14px',
    borderRadius: '20px',
    border: '1.5px solid #8b6a50',
    background: active ? '#8b6a50' : '#f5ece0',
    color: active ? '#fff' : '#5c3d2e',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    whiteSpace: 'nowrap',
  })

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      <button onClick={() => onSelect('')} style={btnStyle(activeYear === '')}>
        Усі роки
      </button>
      {visible.map(year => (
        <button
          key={year}
          onClick={() => onSelect(String(year))}
          style={btnStyle(activeYear === String(year))}
        >
          {year}
        </button>
      ))}
      {hidden > 0 && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            flexShrink: 0,
            padding: '7px 14px',
            borderRadius: '20px',
            border: '1.5px dashed #8b6a50',
            background: 'transparent',
            color: '#8b6a50',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          {expanded ? '▴ згорнути' : `▾ ще ${hidden}`}
        </button>
      )}
    </div>
  )
}

// ── SpeakerDropdown ───────────────────────────────────────────────────────────

const SpeakerDropdown = ({ people, activeSpeakerId, onSelect, mobile }) => {
  const [query, setQuery] = useState('')
  const [open, setOpen]   = useState(false)
  const ref               = useRef(null)

  const entries = Object.entries(people).sort((a, b) =>
    (a[1].nameUk || '').localeCompare(b[1].nameUk || '', 'uk')
  )

  const activeName = activeSpeakerId
    ? (people[activeSpeakerId]?.nameUk || activeSpeakerId)
    : ''

  const filtered = entries.filter(([, p]) =>
    !query || (p.nameUk || '').toLowerCase().includes(query.toLowerCase())
  )

  // close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (id) => {
    onSelect(id)
    setQuery('')
    setOpen(false)
  }

  const inputStyle = {
    width: '100%',
    padding: '8px 32px 8px 14px',
    borderRadius: '20px',
    border: `1.5px solid ${open ? '#8b6a50' : '#c0a882'}`,
    background: '#f5ece0',
    color: activeSpeakerId ? '#2d1f14' : '#9a7c63',
    fontSize: '13px',
    fontWeight: activeSpeakerId ? '600' : '400',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  }

  return (
    <div ref={ref} style={{ position: 'relative', width: mobile ? '100%' : '220px', flexShrink: 0 }}>
      <input
        type='text'
        value={open ? query : activeName}
        placeholder='Усі проповідники'
        onFocus={() => { setOpen(true); setQuery('') }}
        onChange={e => setQuery(e.target.value)}
        style={inputStyle}
      />
      {/* chevron / clear */}
      <span
        onClick={() => { if (activeSpeakerId) { handleSelect('') } else { setOpen(o => !o) } }}
        style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          cursor: 'pointer', fontSize: '11px', color: '#8b6a50', userSelect: 'none',
        }}
      >
        {activeSpeakerId ? '✕' : '▾'}
      </span>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1.5px solid #e0cdb8', borderRadius: '14px',
          boxShadow: '0 8px 24px rgba(92,61,46,0.15)',
          maxHeight: '220px', overflowY: 'auto',
        }}>
          <div
            onMouseDown={() => handleSelect('')}
            style={{
              padding: '9px 14px', fontSize: '13px', color: '#9a7c63',
              cursor: 'pointer', borderBottom: '1px solid #f0e4d4',
              fontFamily: '"Segoe UI", system-ui, sans-serif',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#faf3ea'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            Усі проповідники
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: '9px 14px', fontSize: '13px', color: '#b8a090',
              fontFamily: '"Segoe UI", system-ui, sans-serif' }}>
              Нічого не знайдено
            </div>
          )}
          {filtered.map(([id, person]) => (
            <div
              key={id}
              onMouseDown={() => handleSelect(id)}
              style={{
                padding: '9px 14px', fontSize: '13px', cursor: 'pointer',
                color: id === activeSpeakerId ? '#c0622a' : '#2d1f14',
                fontWeight: id === activeSpeakerId ? '700' : '400',
                fontFamily: '"Segoe UI", system-ui, sans-serif',
                background: id === activeSpeakerId ? '#fdf6ee' : '',
              }}
              onMouseEnter={e => { if (id !== activeSpeakerId) e.currentTarget.style.background = '#faf3ea' }}
              onMouseLeave={e => { if (id !== activeSpeakerId) e.currentTarget.style.background = '' }}
            >
              {person.nameUk || person.nameEn || id}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── SearchInput ───────────────────────────────────────────────────────────────

const SearchInput = ({ value, onChange, mobile }) => (
  <input
    type='text'
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder='Пошук...'
    style={{
      width: mobile ? '100%' : '240px',
      padding: '8px 14px',
      borderRadius: '20px',
      border: '1.5px solid #c0a882',
      background: '#f5ece0',
      color: '#2d1f14',
      fontSize: '13px',
      fontFamily: '"Segoe UI", system-ui, sans-serif',
      outline: 'none',
      boxSizing: 'border-box',
    }}
  />
)

// ── ArchiveEmpty ──────────────────────────────────────────────────────────────

const ArchiveEmpty = ({ onReset }) => (
  <div
    style={{
      textAlign: 'center',
      padding: '60px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
    }}
  >
    <div
      style={{
        fontSize: '48px',
        opacity: 0.4,
      }}
    >
      📭
    </div>
    <p
      style={{
        fontSize: '16px',
        color: '#8b6a50',
        margin: 0,
        fontFamily: '"Segoe UI", system-ui, sans-serif',
      }}
    >
      Нічого не знайдено за вибраними фільтрами
    </p>
    <button
      onClick={onReset}
      style={{
        padding: '10px 28px',
        borderRadius: '20px',
        border: 'none',
        background: '#c0622a',
        color: '#fff',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        fontFamily: '"Segoe UI", system-ui, sans-serif',
      }}
    >
      Скинути фільтри
    </button>
  </div>
)

// ── WebArchive (main) ─────────────────────────────────────────────────────────

export const WebArchive = () => {
  const [videos, setVideos]               = useState([])
  const [people, setPeople]               = useState({})
  const [loading, setLoading]             = useState(true)
  const [filterCategory, setFilterCategory] = useState('')
  const [filterYear, setFilterYear]       = useState('')
  const [filterSpeakerId, setFilterSpeakerId] = useState('')
  const [searchTerm, setSearchTerm]       = useState('')
  const [mobile, setMobile]               = useState(window.innerWidth < 600)
  const [tablet, setTablet]               = useState(window.innerWidth < 900)

  // Responsive breakpoints
  useEffect(() => {
    const onResize = () => {
      setMobile(window.innerWidth < 600)
      setTablet(window.innerWidth < 900)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Load archivePeople once
  useEffect(() => {
    getDocs(collection(db, 'archivePeople'))
      .then(snap => {
        const map = {}
        snap.docs.forEach(d => { map[d.id] = d.data() })
        setPeople(map)
      })
      .catch(() => {})
  }, [])

  // Subscribe to archive collection — normalise legacy field names on the fly
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'archive'), orderBy('year', 'desc')),
      snap => {
        setVideos(snap.docs.map(d => {
          const raw = d.data()
          // legacy → new field mapping
          return {
            id: d.id,
            ...raw,
            // title: old docs use `title`, new use `titleUk`
            titleUk:    raw.titleUk    || raw.title    || '',
            titleEn:    raw.titleEn    || '',
            // category: old docs use `type` ('sermon','word','online','choir'), new use category key
            category:   raw.category   || LEGACY_CATEGORY_MAP[raw.type] || raw.type || '',
            // videoId: old docs may not have it — derive from link
            videoId:    raw.videoId    || extractVideoId(raw.link || ''),
            // speakerIds: old docs use `author` string — wrap in array for display fallback
            speakerIds: raw.speakerIds || [],
            _authorLegacy: raw.author  || '',
          }
        }))
        setLoading(false)
      },
      () => setLoading(false)
    )
    return unsub
  }, [])

  // Derived: available years
  const availableYears = useMemo(
    () => [...new Set(videos.map(v => v.year))].filter(Boolean).sort((a, b) => b - a),
    [videos]
  )

  // Derived: people map — merge archivePeople collection with legacy `author` strings from video docs
  const allPeople = useMemo(() => {
    const legacyMap = {}
    videos.forEach(v => {
      if (v._authorLegacy) legacyMap[v._authorLegacy] = { nameUk: v._authorLegacy }
    })
    return { ...legacyMap, ...people }
  }, [videos, people])

  // Derived: filtered videos
  const filtered = useMemo(() => {
    return videos.filter(v => {
      if (filterCategory && v.category !== filterCategory) return false
      if (filterYear && v.year !== Number(filterYear)) return false
      if (filterSpeakerId) {
        const inNew = (v.speakerIds || []).includes(filterSpeakerId)
        const inLegacy = v._authorLegacy === filterSpeakerId
        if (!inNew && !inLegacy) return false
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase()
        const speakerMatch =
          (v._authorLegacy || '').toLowerCase().includes(q) ||
          (v.speakerIds || []).some(id =>
            (people[id]?.nameUk || '').toLowerCase().includes(q) ||
            (people[id]?.nameEn || '').toLowerCase().includes(q)
          )
        return (
          (v.titleUk || '').toLowerCase().includes(q) ||
          (v.titleEn || '').toLowerCase().includes(q) ||
          (v.bible || '').toLowerCase().includes(q) ||
          (v.keywords || []).some(k => k.toLowerCase().includes(q)) ||
          speakerMatch
        )
      }
      return true
    }).sort((a, b) => {
      const da = a.date?.toDate?.() ?? new Date(a.year || 0, 0, 1)
      const db = b.date?.toDate?.() ?? new Date(b.year || 0, 0, 1)
      return db - da
    })
  }, [videos, people, filterCategory, filterYear, filterSpeakerId, searchTerm])

  const handleReset = () => {
    setFilterCategory('')
    setFilterYear('')
    setFilterSpeakerId('')
    setSearchTerm('')
  }

  const cols = mobile ? '1fr' : tablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'

  return (
    <>
      <ArchiveHero mobile={mobile} />

      <div
        style={{
          maxWidth: '1100px',
          margin: '0 auto 60px',
          padding: '24px 16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          fontFamily: '"Segoe UI", system-ui, sans-serif',
        }}
      >
        {/* Filter bar */}
        <div
          style={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            flexWrap: mobile ? undefined : 'wrap',
            gap: mobile ? '10px' : '16px',
            alignItems: mobile ? 'stretch' : 'flex-start',
          }}
        >
          <CategoryPills activeCategory={filterCategory} onToggle={setFilterCategory} />
          <YearButtons years={availableYears} activeYear={filterYear} onSelect={setFilterYear} />
          <SpeakerDropdown
            people={allPeople}
            activeSpeakerId={filterSpeakerId}
            onSelect={setFilterSpeakerId}
            mobile={mobile}
          />
          <SearchInput value={searchTerm} onChange={setSearchTerm} mobile={mobile} />
        </div>

        {/* Loading */}
        {loading && (
          <p
            style={{
              textAlign: 'center',
              padding: '60px 0',
              color: '#a07850',
              fontSize: '18px',
              letterSpacing: '4px',
            }}
          >
            •••
          </p>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <ArchiveEmpty onReset={handleReset} />
        )}

        {/* Grid */}
        {!loading && filtered.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: cols,
              gap: '20px',
            }}
          >
            {filtered.map(video => {
              const speakerNames = (video.speakerIds || [])
                .map(id => people[id]?.nameUk || people[id]?.nameEn || null)
                .filter(Boolean)
              return (
                <VideoCard
                  key={video.id}
                  video={video}
                  speakerNames={speakerNames}
                  mobile={mobile}
                />
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
